import 'mathlive' // mathlive import하지 않으면 MathfieldElement method 사용할 수 없음

import { ANSWER_TYPE } from '@mathflat/domain/@entities/Problem/constants'
import { debounce } from 'lodash-es'
import type { MathfieldElement } from 'mathlive'
import { observer } from 'mobx-react'
import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react'

import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { CustomEventService } from '~/@common/services/event.service'
import Input from '~/@common/ui/(Input)/Input/Input'
import { VirtualKeyboard } from '~/@common/ui/(Keypad)'
import type { VirtualKeyboardService } from '~/@common/ui/(Keypad)/keypad.service'
import { virtualKeyboardService } from '~/@common/ui/(Keypad)/keypad.service'
import Checkbox from '~/@common/ui/checkbox'
import type { ProblemScoring } from '~/@pages/@common/(ProblemScoring)/@trait/ProblemScoring.trait'

import { postRefineAnswer } from './_util'
import S from './자동채점입력.style'
import { 모름Circle } from './자동채점입력_객관식'

// 주관식, controlled only
export type 자동채점입력_주관식Props = {
  id: ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['id']
  value: string | typeof ANSWER_TYPE.UNKNOWN | ''
  onChange?: (value: 자동채점입력_주관식Props['value']) => void
  keypadTypes: NonNullable<ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['keypadTypes']>
  schoolType: ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['contentSchoolType']
  answerUnits: ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['answerUnits']
  virtualKeyboardMarginBottom?: CSSProperties['marginBottom']
  onCloseCallback?: () => void
} & Pick<MathFieldProps, 'virtualKeybaordShowType'>

export const 자동채점입력_주관식 = observer(
  ({
    id,
    value: _value,
    keypadTypes,
    schoolType,
    onChange,
    virtualKeybaordShowType,
    answerUnits,
    virtualKeyboardMarginBottom = 5,
    onCloseCallback,
  }: 자동채점입력_주관식Props) => {
    const scrollRef = useRef<HTMLDivElement | null>(null)

    const prefixUnit = useMemo(() => {
      const unit = answerUnits?.find((item) => item.index === 0)?.unit
      if (!unit) return ''
      return getRefinedUnit(unit, false)
    }, [answerUnits])

    const postfixUnit = useMemo(() => {
      const unit = answerUnits?.find((item) => item.index > 0)?.unit
      if (!unit) return ''
      return getRefinedUnit(unit, false)
    }, [answerUnits])

    const isActiveProblem =
      id &&
      virtualKeyboardService.activeProblemScoringId &&
      String(id) === String(virtualKeyboardService.activeProblemScoringId)

    useEffect(() => {
      return () => {
        if (!virtualKeyboardService.show && !!isActiveProblem) {
          onCloseCallback?.()
        }
      }
    }, [virtualKeyboardService.show, onCloseCallback])

    return (
      <>
        <S.AutoMarkingContainer
          ref={scrollRef}
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          {virtualKeyboardService.show &&
            virtualKeyboardService.showType === 'fixed' &&
            isActiveProblem && (
              <VirtualKeyboard
                style={{
                  position: 'absolute',
                  left: 0,
                  bottom: '100%',
                  marginBottom: virtualKeyboardMarginBottom,
                }}
                closeKeyboard={() => virtualKeyboardService.close()}
                onKeyButtonClick={(e, latexNodeType) => {
                  virtualKeyboardService.onKeyButtonClick(e, latexNodeType)
                }}
                keypadType={virtualKeyboardService.type}
              />
            )}
          <div className="input-wrapper" onClick={(e) => e.stopPropagation()}>
            {prefixUnit !== '' && <span className="auto-marking-input-prefix">{prefixUnit}</span>}
            {keypadTypes?.includes('TEXT') ? (
              <Input
                value={_value === ANSWER_TYPE.UNKNOWN ? '' : _value}
                onChange={(e) => onChange?.(e.target.value)}
              />
            ) : (
              <MathField
                id={id}
                value={_value}
                keypadTypes={keypadTypes}
                schoolType={schoolType}
                onChange={onChange}
                virtualKeybaordShowType={virtualKeybaordShowType}
              />
            )}

            {postfixUnit !== '' && (
              <span className="auto-marking-input-postfix">{postfixUnit}</span>
            )}
          </div>
          <Checkbox.Group
            values={[_value]}
            onChange={(values) => {
              if (values.includes(ANSWER_TYPE.UNKNOWN)) {
                // 모름체크 되었을 때
                // ref.current?.setValue('')
                onChange?.(ANSWER_TYPE.UNKNOWN)
                onCloseCallback?.()
                return
              }

              onChange?.('')
            }}
          >
            <모름Circle
              onClick={() => {
                document.body.click()
              }}
              className="unknown-short-answer-circle"
            />
          </Checkbox.Group>
        </S.AutoMarkingContainer>
      </>
    )
  },
)

type MathFieldProps = {
  id: number | string
  value: string
  keypadTypes: NonNullable<ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['keypadTypes']>
  schoolType: ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['contentSchoolType']
  onChange?(v: string): void
  virtualKeybaordShowType?: VirtualKeyboardService['showType']
}

const MathField = observer(
  ({ id, value, keypadTypes, schoolType, onChange, virtualKeybaordShowType }: MathFieldProps) => {
    const ref = useRef<MathfieldElement | null>(null)

    const { isMobile } = useStudentAppMediaQuery()
    const [isActive, setIsActive] = useState(false)

    const _virtualKeybaordShowType = virtualKeybaordShowType
      ? virtualKeybaordShowType
      : !isMobile
        ? 'floater'
        : 'drawer'

    useEffect(() => {
      if (ref.current) {
        // math-field element를 module에 등록하고, 구체적인 로직은 module에서 처리합니다.
        const shadowChild = ref.current.shadowRoot?.querySelector('div')
        if (shadowChild) {
          // mathlive 기본 스타일링 shadowRoot > div의 display 값을 바꿔야해서. (css로 안되었음)
          shadowChild.style.display = 'flex'
        }
        ref.current.setOptions({
          defaultMode: 'math',
          fontsDirectory: `https://mathflat-assets.s3.ap-northeast-2.amazonaws.com/font`,
          keypressSound: null,
          plonkSound: null,
          virtualKeyboardMode: 'off',
          placeholderSymbol: '■',
          fractionNavigationOrder: 'denominator-numerator', // 커서 분모-분자 순서로 이동하는 옵션
          // mathlive 내 스타일은 이렇게밖에 overwrite이 안 됨.
          createHTML: (html) => `<style>
    .ML__mathlive, .ML__cmr, ML__base{font-family: 'Spoqa Han Sans Neo', system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    'Open Sans',
    'Helvetica Neue',
    sans-serif !important; font-weight:400; }
    .mfrac{font-size:10px;}</style>${html}`,
        })
      }
    }, [])

    useEffect(() => {
      if (value === ANSWER_TYPE.UNKNOWN) {
        ref.current?.setValue('')
        return
      }
      // const ANSWER_LIMIT_COUNT = 30 // 자동채점 30자 제한 => value에서 제한을 걸면 분수의 형태도('\frac{\placeholder{}}{\placeholder{}}') value도 길이 제한에 걸리므로 재수정할 필요가 있음
      // if (value.length >= ANSWER_LIMIT_COUNT) {
      //   ref.current?.setValue(value.slice(0, ANSWER_LIMIT_COUNT))
      //   return
      // }
      if (value !== ref.current?.getValue()) {
        ref.current?.setValue(value)
      }
    }, [value])

    useEffect(() => {
      const handleInput = (ev) => {
        if (ref.current) {
          if (value === ANSWER_TYPE.UNKNOWN && ref.current.getValue() === '') {
            return
          }
          onChange?.(ev.target.value)
        }
      }
      const handleFocus = debounce((e) => {
        e.stopPropagation()
        if (ref.current) {
          if (
            !virtualKeyboardService.show ||
            virtualKeyboardService.id !== `virtual-keyboard-${id}`
          ) {
            virtualKeyboardService.open({
              el: ref.current,
              keypadTypes,
              schoolType,
              showType: _virtualKeybaordShowType,
              problemScoringId: String(id),
            })
          }
        }
      }, 100)

      ref.current?.addEventListener('input', handleInput)
      ref.current?.addEventListener('focus', handleFocus)

      return () => {
        handleFocus.cancel()
        ref.current?.removeEventListener('input', handleInput)
        ref.current?.removeEventListener('focus', handleFocus)
      }
    }, [id, keypadTypes, schoolType, _virtualKeybaordShowType, onChange, value])

    useEffect(() => {
      if (ref.current && !isActive && value !== ANSWER_TYPE.UNKNOWN) {
        onChange?.(postRefineAnswer(ref.current.getValue()))
      }
    }, [isActive, value])

    useEffect(() => {
      if (!virtualKeyboardService.show) {
        setIsActive(false)
      } else {
        setIsActive(virtualKeyboardService.id === `virtual-keyboard-${id}`)
      }
    }, [virtualKeyboardService.show, virtualKeyboardService.id])

    return (
      <math-field
        data-tooltip-id={`auto-scoring-${id}`}
        data-virtual-keyboard-id={`virtual-keyboard-${id}`}
        ref={ref}
        onClick={(e) => {
          if (ref.current) {
            ref.current.focus()
          }
        }}
        keypress-sound={'null'}
        plonk-sound={'null'}
        onKeyUp={(e) => {
          if (e.key === 'Escape') {
            ref.current?.executeCommand('deleteBackward')
            virtualKeyboardService.close()
            CustomEventService.alltooltipOff.dispatch()
            return
          }
        }}
      />
    )
  },
)

const unitRegex = /(\[(.+)])/

const getRefinedUnit = (unit, includeBracket) => {
  const match = unit.match(unitRegex)

  if (match?.length < 1) {
    return ''
  }

  return (includeBracket ? match[1] : match[2]) || ''
}
